import React, { useRef, useState } from "react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import ConfirmDeleteModal from "../../components/images/ConfirmDeleteModal";
import useAuth from "src/hooks/useAuth";
import useQueryParams from "../../hooks/useQueryParams";

export default function CaptureActionsScrubber({
  disabled,
  onSave,
  onCreate,
  onDuplicate,
  onRemove,
  onTransfer,
  disableSave,
  onCloseModal,
  handeRefetch,
  canWrite = true,
  canRemove = true,
  showOnlySave = false,
  hideAddNew = false,
}) {
  const actionLockRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const { user } = useAuth();
  const { settings } = user?.company || {};
  const params = useQueryParams();
  const isReceivingWizardDisabled = params.get("disable_receiving_wizard") === "true";

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const isBusy = disabled || busyAction !== null;
  const runAction = (actionName, action, onSuccess) => async () => {
    if (!action || disabled || actionLockRef.current) return;
    actionLockRef.current = true;
    setBusyAction(actionName);
    try {
      const result = await action();
      if (result !== undefined && onSuccess) onSuccess(result);
      return result;
    } catch {
      return undefined;
    } finally {
      actionLockRef.current = false;
      setBusyAction(null);
    }
  };

  const handleDelete = () => {
    onRemove().then(() => {
      handeRefetch();
      onCloseModal();
      setShowModal(false);
    });
  };

  const showRemove = !showOnlySave && onRemove && canRemove;
  const showDuplicate =
    !showOnlySave &&
    onDuplicate &&
    (!settings.receiving_wizard || isReceivingWizardDisabled) &&
    canWrite;
  const showSave = onSave && canWrite;
  const showCreate = !showOnlySave && !hideAddNew && onCreate && canWrite;

  if (showOnlySave) {
    return (
      <Box flex={1}>
        <Button
          disabled={isBusy || !onSave || disableSave}
          id="saveAsset"
          data-id="save-asset"
          fullWidth
          variant={showOnlySave ? "contained" : "outlined"}
          onClick={runAction("save", onSave)}
        >
          Save
        </Button>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" gap={1}>
          {showRemove && (
            <Box flex={1}>
              <Button
                disabled={isBusy || !onRemove}
                id="RemoveAsset"
                data-id="remove-asset"
                fullWidth
                variant="outlined"
                onClick={handleOpenModal}
              >
                Delete
              </Button>
            </Box>
          )}

          {showDuplicate && (
            <Box flex={1}>
              <Button
                disabled={isBusy || !onDuplicate}
                id="duplicateAsset"
                data-id="duplicate-asset"
                fullWidth
                variant="outlined"
                onClick={runAction("duplicate", onDuplicate, () => {
                  handeRefetch();
                })}
              >
                Duplicate
              </Button>
            </Box>
          )}
          {showSave && (
            <Box flex={1}>
              <Button
                disabled={isBusy || !onSave || disableSave}
                id="saveAsset"
                data-id="save-asset"
                fullWidth
                variant="outlined"
                onClick={runAction("save", onSave)}
              >
                Save
              </Button>
            </Box>
          )}
          {showCreate && (
            <Box flex={1}>
              <Button
                disabled={isBusy || !onCreate}
                id="addNewAsset"
                data-id="add-new-asset"
                fullWidth
                variant="contained"
                color="primary"
                onClick={runAction("create", onCreate)}
              >
                Add New
              </Button>
            </Box>
          )}
        </Box>
      </CardContent>

      {showModal && (
        <ConfirmDeleteModal
          title="Delete Asset"
          descr="Are you sure you want to delete the selected asset?"
          onClose={handleCloseModal}
          onDelete={handleDelete}
        />
      )}
    </Card>
  );
}
